<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <User class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Personal information
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent>
            <div class="space-y-6">
                <div class="space-y-6">
                    <div class="w-full max-w-[373px]">
                        <Label for="name" class="text-sm font-medium text-foreground">Name</Label>
                        <Input
                            id="name"
                            v-model="form.name"
                            class="mt-2"
                        />
                    </div>
                    <div class="w-full max-w-[373px]">
                        <Label for="email" class="text-sm font-medium text-foreground">Email</Label>
                        <Input
                            id="email"
                            v-model="form.email"
                            type="email"
                            class="mt-2"
                            :disabled="!hasPassword"
                        />
                    </div>
                </div>

                <!-- When email changed, require password confirmation -->
                <ConfirmsPasswordV2
                    v-if="emailChanged"
                    title="Confirm Password"
                    content="Please confirm your current password to change your email address."
                    :skip-confirmation="true"
                    :force-confirmation="true"
                    :allow-passkey="hasPasskeys"
                    @confirmed="submitWithPassword"
                >
                    <Button
                        :disabled="loading || (!nameChanged && !emailChanged)"
                    >
                        Save changes
                    </Button>
                </ConfirmsPasswordV2>

                <!-- When only name changed, no confirmation needed -->
                <Button
                    v-else
                    :disabled="loading || !nameChanged"
                    @click="save()"
                >
                    Save changes
                </Button>
            </div>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { User } from 'lucide-vue-next';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ConfirmsPasswordV2 from './ConfirmsPasswordV2.vue';

const props = withDefaults(defineProps<{
    hasPassword: boolean;
    hasPasskeys?: boolean;
}>(), {
    hasPasskeys: false,
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const loading = ref(false);

const form = ref({
    name: globalProps.value.user.name,
    email: globalProps.value.user.email,
});

const nameChanged = computed(() => form.value.name !== globalProps.value.user.name);
const emailChanged = computed(() => form.value.email !== globalProps.value.user.email);

const submitWithPassword = (password: string) => {
    save(password);
};

const save = (currentPassword?: string) => {
    loading.value = true;

    router.put(
        route('settings.profile-settings.update'),
        {
            name: form.value.name,
            email: form.value.email,
            current_password: currentPassword || '',
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Profile updated successfully!');
            },
            onError: (err) => {
                const msg = err?.updateProfileInformation?.email
                    || err?.updateProfileInformation?.name
                    || err?.updateProfileInformation?.current_password
                    || 'Failed to update profile';
                toast.error(msg);
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
